import { cn } from "../../lib/utils";
import { Link } from "react-router-dom";
import React, { useState, createContext, useContext, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { 
  Menu, 
  X, 
  Car, 
  MapPin, 
  Coins, 
  CalendarHeart,
  User,
  UserCog,
  Megaphone,
  Settings,
  ChevronUp,
  Award,
  Zap
} from "lucide-react";
import { useSyncedUser } from '../../context/SyncedUserContext';
import { GB, NL, FR, DE } from 'country-flag-icons/react/3x2';

// Define icon colors based on your features page
const ICON_COLORS = {
  advalvas: "text-blue-500",
  discover: "text-green-500",
  parking: "text-purple-500",
  leaderboard: "text-amber-500",
  profile: "text-indigo-500",
  management: "text-blue-600",
  settings: "text-neutral-600",
};

const SidebarContext = createContext();

const languages = [
  { code: 'en', label: 'English', flag: <GB className="w-5 h-5" /> },
  { code: 'nl', label: 'Nederlands', flag: <NL className="w-5 h-5" /> },
  { code: 'fr', label: 'Français', flag: <FR className="w-5 h-5" /> },
  { code: 'de', label: 'Deutsch', flag: <DE className="w-5 h-5" /> }
];

const Sidebar = ({ children, open, setOpen }) => (
  <SidebarContext.Provider value={{ open, setOpen }}>
    {children}
  </SidebarContext.Provider>
);

const SidebarBody = ({ className, children, ...props }) => (
  <>
    <DesktopSidebar className={className} {...props}>{children}</DesktopSidebar>
    <MobileBottomNav />
  </>
);const DesktopSidebar = ({ className, children, ...props }) => {
  const { open, setOpen } = useContext(SidebarContext);
  return (
    <motion.div
      className={cn(
        "h-full px-4 py-4 hidden md:flex md:flex-col bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800",
        className
      )}
      initial={false}
      animate={{ 
        width: open ? "300px" : "80px",
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        duration: 0.2
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// New Mobile Bottom Navigation
const MobileBottomNav = () => {
  const { syncedUserData } = useSyncedUser();
  const userRole = syncedUserData?.user?.role;
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { t } = useTranslation();
  
  // Main navigation links
  const mainLinks = [
    {
      label: t('navigation.advalvas'),
      href: "/",
      icon: <Megaphone className="h-6 w-6" />,
      color: ICON_COLORS.advalvas
    },
    {
      label: t('navigation.discover'),
      href: "/discover",
      icon: <MapPin className="h-6 w-6" />,
      color: ICON_COLORS.discover
    },
    {
      label: t('navigation.parking'),
      href: "/parking",
      icon: <Car className="h-6 w-6" />,
      color: ICON_COLORS.parking
    },
    {
      label: t('navigation.profile'),
      href: "/profile",
      icon: <User className="h-6 w-6" />,
      color: ICON_COLORS.profile
    },
  ];
  
  // Extra links that will appear in the "More" menu
  const extraLinks = [
    {
      label: t('navigation.management'),
      href: "/management",
      icon: <UserCog className="h-6 w-6" />,
      color: ICON_COLORS.management,
      hidden: userRole !== 2,
    },
    {
      label: t('navigation.leaderboard'),
      href: "/leaderboard",
      icon: <Award className="h-6 w-6" />,
      color: ICON_COLORS.leaderboard,
    },
    {
      label: t('navigation.settings'),
      href: "/settings",
      icon: <Settings className="h-6 w-6" />,
      color: ICON_COLORS.settings,
    }
  ].filter(link => !link.hidden);

  const bottomNavItem = (link) => {
    const isActive = location.pathname === link.href;
    return (
      <Link
        key={link.href}
        to={link.href}
        className={cn(
          "flex flex-1 flex-col items-center justify-center py-1",
          isActive && link.color
        )}
      >
        {React.cloneElement(link.icon, {
          className: cn(
            isActive ? link.color : "text-neutral-500",
            "mb-1"
          )
        })}
        <span className={cn(
          "text-xs",
          isActive ? `${link.color} font-medium` : "text-neutral-500"
        )}>
          {link.label}
        </span>
      </Link>
    );
  };

  return (
    <div className="md:hidden">
      {/* Main bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 z-30 flex items-center h-16">
        {mainLinks.map(bottomNavItem)}
        
        {/* More button */}
        {extraLinks.length > 0 && (
          <button
            className={cn(
              "flex flex-1 flex-col items-center justify-center py-1",
              moreOpen && "text-blue-500"
            )}
            onClick={() => setMoreOpen(!moreOpen)}
          >
            <ChevronUp className={cn(
              moreOpen ? "text-blue-500 rotate-0" : "text-neutral-500 rotate-180",
              "mb-1 transition-transform"
            )} />
            <span className={cn(
              "text-xs",
              moreOpen ? "text-blue-500 font-medium" : "text-neutral-500"
            )}>
              {t('navigation.more')}
            </span>
          </button>
        )}
      </div>

      {/* More menu drawer */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 400, damping: 40 }}
            className="fixed bottom-16 left-0 right-0 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 z-20 rounded-t-xl shadow-lg"
          >
            <div className="p-2">
              {extraLinks.map(link => {
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg",
                      isActive 
                        ? "bg-gradient-to-r from-blue-500/10 to-indigo-500/10" 
                        : "hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    )}
                    onClick={() => setMoreOpen(false)}
                  >
                    {React.cloneElement(link.icon, {
                      className: isActive ? link.color : "text-neutral-600 dark:text-neutral-400"
                    })}
                    <span className={cn(
                      "font-medium",
                      isActive && link.color
                    )}>
                      {link.label}
                    </span>
                    
                    {link.label === t('navigation.leaderboard') && (
                      <div className="ml-auto flex items-center gap-1 text-amber-500 text-xs">
                        <Zap className="h-3.5 w-3.5 text-amber-500 fill-amber-500/50" />
                      </div>
                    )}
                  </Link>
                );
              })}
              
              {/* Language selector in more menu */}
              <div className="border-t border-neutral-200 dark:border-neutral-800 mt-2 pt-2">
                <div className="p-3">
                  <p className="text-xs text-neutral-500 mb-2">{t('language.title')}</p>
                  <LanguageSelectorMobile />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop for more menu */}
      {moreOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/20 z-10"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* Add padding to the bottom of the content to account for the nav bar */}
      <div className="h-16" />
    </div>
  );
};

// Mobile language selector component
const LanguageSelectorMobile = () => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(() => 
    localStorage.getItem('language') || 'nl'
  );

  const handleLanguageChange = (code) => {
    setLanguage(code);
    localStorage.setItem('language', code);
    i18n.changeLanguage(code);
  };

  useEffect(() => {
    // Initialize language based on localStorage or default to 'nl'
    const storedLanguage = localStorage.getItem('language') || 'nl';
    i18n.changeLanguage(storedLanguage);
    setLanguage(storedLanguage);
  }, [i18n]);

  return (
    <div className="flex flex-wrap gap-2">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => handleLanguageChange(lang.code)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-md text-sm",
            language === lang.code 
              ? "bg-gradient-to-r from-blue-500/20 to-indigo-500/20 text-blue-600" 
              : "bg-neutral-100 dark:bg-neutral-800"
          )}
        >
          {lang.flag}
          <span>{lang.label}</span>
        </button>
      ))}
    </div>
  );
};
const SidebarLink = ({ link, className }) => {
  const { open } = useContext(SidebarContext);
  const location = useLocation();
  const isActive = location.pathname === link.href;

  if (link.hidden) {
    return null;
  }
  
  // Map routes to colors
  let color = "text-primary";
  if (link.href === "/") color = ICON_COLORS.advalvas;
  else if (link.href === "/discover") color = ICON_COLORS.discover;
  else if (link.href === "/parking") color = ICON_COLORS.parking;
  else if (link.href === "/leaderboard") color = ICON_COLORS.leaderboard;
  else if (link.href === "/profile") color = ICON_COLORS.profile;
  else if (link.href === "/management") color = ICON_COLORS.management;
  else if (link.href === "/settings") color = ICON_COLORS.settings;
  
  // Generate active background for the link
  const getActiveBg = () => {
    if (link.href === "/") return "bg-blue-500/10";
    if (link.href === "/discover") return "bg-green-500/10";
    if (link.href === "/parking") return "bg-purple-500/10";
    if (link.href === "/leaderboard") return "bg-amber-500/10";
    if (link.href === "/profile") return "bg-indigo-500/10";
    if (link.href === "/management") return "bg-blue-600/10";
    return "bg-primary/10";
  };
  
  return (
    <Link
      to={link.href}
      className={cn(
        "flex items-center p-2 rounded-lg transition-all relative group",
        open ? "justify-start gap-4" : "justify-center",
        isActive ? 
          getActiveBg() : 
          "hover:bg-neutral-100 dark:hover:bg-neutral-800",
        className
      )}
    >
      <div className={cn(
        "flex items-center",
        open ? "gap-4" : "justify-center",
        isActive && color
      )}>
        {React.cloneElement(link.icon, {
          className: cn(
            isActive ? color : link.icon.props.className,
          )
        })}
        {open && (
          <span className={cn(
            "font-medium",
            isActive && color
          )}>
            {link.label}
           
          </span>
        )}
      </div>
      {isActive && open && (
        <div className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full",
          link.href === "/" && "bg-blue-500",
          link.href === "/discover" && "bg-green-500",
          link.href === "/parking" && "bg-purple-500",
          link.href === "/leaderboard" && "bg-amber-500",
          link.href === "/profile" && "bg-indigo-500",
          link.href === "/management" && "bg-blue-600",
          link.href === "/settings" && "bg-primary",
        )} />
      )}
    </Link>
  );
};

const BuildingLogo = ({ open }) => {
  const { t } = useTranslation();
  return (
    <Link to="/" className="flex items-center h-8 px-2 relative">
      <div className="h-8 w-8 rounded-lg bg-black flex items-center justify-center">
        <span className="text-white font-bold">E</span>
      </div>
      <AnimatePresence mode="wait">
        {open && (
          <motion.span
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            className="ml-2 bg-black font-medium whitespace-nowrap bg-clip-text text-transparent"
          >
            {t('app.title')}
          </motion.span>
        )}
      </AnimatePresence>
    </Link>
  );
};

const LanguageSelector = ({ language, onChange, open }) => {
  if (open) {
    return (
      <select
        value={language}
        onChange={onChange}
        className="w-full appearance-none bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 
                 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20
                 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors cursor-pointer"
      >
        {languages.map((lang) => (
          <option 
            key={lang.code} 
            value={lang.code} 
            className="flex items-center gap-2 py-1"
          >
            {lang.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="h-8 w-8 rounded-lg bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-200 
                    dark:border-blue-800 flex items-center justify-center hover:bg-blue-50 
                    dark:hover:bg-blue-900/20 transition-colors">
      <span className="text-sm">
        {languages.find(l => l.code === language)?.flag}
      </span>
    </div>
  );
};

const SidebarMain = () => {
  const [open, setOpen] = useState(false);
  const { syncedUserData } = useSyncedUser();
  const user = syncedUserData?.user;
  const userRole = syncedUserData?.user?.role;
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(() => 
    localStorage.getItem('language') || 'nl'
  );

  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  // Add useEffect to set language on component mount
  useEffect(() => {
    const storedLanguage = localStorage.getItem('language') || 'nl';
    i18n.changeLanguage(storedLanguage);
    setLanguage(storedLanguage);
  }, [i18n]);
  
  const links = [
    {
      label: t('navigation.advalvas'),
      href: "/",
      icon: <Megaphone className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
    },
    {
      label: t('navigation.discover'),
      href: "/discover",
      icon: <MapPin className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
    },
    {
      label: t('navigation.parking'),
      href: "/parking",
      icon: <Car className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
    },
    {
      label: t('navigation.leaderboard'),
      href: "/leaderboard",
      icon: <Award className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
    },
    {
      label: t('navigation.profile'),
      href: "/profile",
      icon: <User className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
    },
          
    {
      label: t('navigation.management'),
      href: "/management",
      icon: <UserCog className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
      hidden: userRole !== 2,
    },
  ];

  return (
    <div className="h-screen">
      <Sidebar open={open} setOpen={setOpen}>
        <SidebarBody className="justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            <BuildingLogo open={open} />
            <div className="mt-8 flex flex-col gap-2">
              {links.map((link, idx) => (
                <SidebarLink key={idx} link={link} />
              ))}
            </div>
          </div>
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            {/* Language Selector */}
            <div className="px-2 py-2">
              <LanguageSelector 
                language={language}
                onChange={handleLanguageChange}
                open={open}
              />
            </div>
            <SidebarLink
              link={{
                label: user?.username,
                href: "/profile",
                icon: (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {user?.username?.split('.').map(part => part.charAt(0).toUpperCase()).join('')}
                    </span>                 
                  </div>
                ),
              }}
            />
          </div>
        </SidebarBody>
      </Sidebar>
    </div>
  );
};

export { Sidebar, SidebarBody, SidebarLink };
export default SidebarMain;